import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Chip,
  ClickAwayListener,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';
import { defineMessages, useIntl, type MessageDescriptor } from 'react-intl';

import { borderRadius } from '@verantos-dev/shared-ui-components/tokens';

import { useProjectPanelLayoutContext } from '../context/ProjectPanelLayoutContext';
import { useElementWidth } from '../hooks/useElementWidth';
import type {
  ProjectArtifactFilterKind,
  ProjectArtifactKind,
  ProjectArtifactScopeFilter,
} from '../types';
import { ARTIFACT_FILTER_ICON, ARTIFACT_KIND_ICON, ARTIFACT_SCOPE_ICON } from './artifactKindIcons';

interface FilterChipDef {
  id: ProjectArtifactFilterKind;
  label: string;
}

interface ScopeFacetDef {
  id: ProjectArtifactScopeFilter;
  label: MessageDescriptor;
}

/** Scope-facet counts keyed by facet id (OR-6615). */
export type ProjectArtifactScopeCounts = Record<ProjectArtifactScopeFilter, number>;

// OR-6615 scope-facet labels — i18n via react-intl (default messages inline so
// they render without a registered catalog and are extractable for translation).
const scopeFacetMessages = defineMessages({
  all: { id: 'projectArtifacts.scope.all', defaultMessage: 'All scopes' },
  project: { id: 'projectArtifacts.scope.project', defaultMessage: 'Project' },
  organization: { id: 'projectArtifacts.scope.organization', defaultMessage: 'Organization' },
});

const SCOPE_FACETS: ScopeFacetDef[] = [
  { id: 'all', label: scopeFacetMessages.all },
  { id: 'project', label: scopeFacetMessages.project },
  { id: 'organization', label: scopeFacetMessages.organization },
];

/**
 * Per-chip artifact counts keyed by filter kind. `all` is the total across
 * the (flag-visible) kinds. Derived synchronously from the already-loaded
 * artifact manifest by the page — see `ProjectArtifactsListPage`.
 */
export type ProjectArtifactCounts = Record<ProjectArtifactFilterKind, number>;

const FILTER_CHIPS: FilterChipDef[] = [
  { id: 'all', label: 'All types' },
  { id: 'analytical_dataset', label: 'Analytical datasets' },
  { id: 'cohort', label: 'Cohorts' },
  { id: 'variable', label: 'Variables' },
  { id: 'concept_group', label: 'Concept groups' },
  { id: 'analysis', label: 'Analyses' },
];

// While the flag is on, analytical datasets fold into the "Analyses" chip
// (no chip of their own) and the "Analyses" chip appears; while it's off,
// datasets keep their own chip and "Analyses" is hidden — pre-flag behavior.
// TODO: cleanup with descriptive_analysis_impl ff
const filterChipsFor = (descriptiveAnalysisImplEnabled: boolean): FilterChipDef[] =>
  descriptiveAnalysisImplEnabled
    ? FILTER_CHIPS.filter((chip) => chip.id !== 'analytical_dataset')
    : FILTER_CHIPS.filter((chip) => chip.id !== 'analysis');

/**
 * Width thresholds (in px, observed on the toolbar root) at which we
 * progressively collapse controls. Order of collapse as the panel narrows:
 * **search first, then create, then chips**. Filter chips stay inline
 * the longest (they wrap into a second row at borderline widths) because
 * they're the primary navigation in this list.
 *
 * Tuned against the project page's resizable content panel — calibrate
 * with VDS designer if chip labels or the right-side cluster change.
 *
 * Controls collapse strictly in this order as the toolbar narrows:
 * **search first, then create, then chips**. `FULL` is set above the
 * width at which the *widest* chip strip (all five filter chips, incl.
 * "Analytical datasets", with the inline search field still mounted)
 * would overflow and wrap onto a second row (~1078px measured). Collapsing
 * the search field to an icon at that point frees ~170px on the right
 * cluster, which keeps the chips on one line well past the point where
 * Create and the chips themselves later collapse.
 *
 * - `>= FULL`: chips inline, search field, Create with label.
 * - `FULL > w >= CREATE_LABEL`: chips inline (may wrap), search → icon,
 *   Create with label.
 * - `CREATE_LABEL > w >= CHIPS_INLINE_FLOOR`: chips inline (may wrap),
 *   search icon, Create → icon (hover to reveal label).
 * - `< CHIPS_INLINE_FLOOR`: chips → Filter dropdown, search icon,
 *   Create icon.
 */
/** @internal Exported for boundary tests; not part of the component API. */
export const TOOLBAR_BREAKPOINT_CHIPS_INLINE_FLOOR = 580;
/** @internal Exported for boundary tests; not part of the component API. */
export const TOOLBAR_BREAKPOINT_CREATE_LABEL = 680;
/** @internal Exported for boundary tests; not part of the component API. */
export const TOOLBAR_BREAKPOINT_FULL = 1090;

export type ToolbarLayoutMode = 'full' | 'searchIcon' | 'createIcon' | 'narrow';

/** @internal Exported for boundary tests; not part of the component API. */
export const resolveLayout = (width: number): ToolbarLayoutMode => {
  // width === 0 happens before observation; treat as "full" so first paint
  // and tests that don't drive layout see the canonical layout.
  if (width === 0 || width >= TOOLBAR_BREAKPOINT_FULL) return 'full';
  if (width >= TOOLBAR_BREAKPOINT_CREATE_LABEL) return 'searchIcon';
  if (width >= TOOLBAR_BREAKPOINT_CHIPS_INLINE_FLOOR) return 'createIcon';
  return 'narrow';
};

export interface CreateOption {
  id: ProjectArtifactKind;
  label: string;
  onSelect: () => void;
}

export interface ProjectArtifactsToolbarProps {
  activeFilter: ProjectArtifactFilterKind;
  onFilterChange: (next: ProjectArtifactFilterKind) => void;
  search: string;
  onSearchChange: (next: string) => void;
  createOptions: CreateOption[];
  /** Per-kind counts rendered in each filter chip label, e.g. `Cohorts (4)`. */
  counts: ProjectArtifactCounts;
  /** While true the counts are still resolving; chips show a count skeleton
   *  instead of a number. */
  countsLoading: boolean;
  /** Hide the filter chips row — used for the zero-assets welcome state,
   *  where there's nothing to filter. Defaults to true. */
  showFilters?: boolean;
  /** Hide the search field — used alongside `showFilters={false}` for the
   *  zero-assets welcome state. Defaults to true. */
  showSearch?: boolean;
  /** Hide the Create button — used when this instance renders the
   *  chips/search-only row *inside* the grid's bordered card, with a
   *  second, Create-only instance of this component (`showFilters`/
   *  `showSearch` both false) rendered in its own unbordered row above
   *  the card instead (mockup 80:12368). Defaults to true. */
  showCreate?: boolean;
  /** Drop the bottom divider — used when the toolbar sits above the grid's
   *  bordered card rather than as its top row (zero-assets welcome state,
   *  or the Create-only row above a populated card). Defaults to true. */
  bordered?: boolean;
  /** OR-6615: the scope facet row (All scopes / Project / Organization) under
   *  the kind chips, rendered whenever ``showFilters`` is true. Requires
   *  ``activeScopeFilter`` / ``onScopeFilterChange`` / ``scopeCounts``. */
  activeScopeFilter?: ProjectArtifactScopeFilter;
  onScopeFilterChange?: (next: ProjectArtifactScopeFilter) => void;
  scopeCounts?: ProjectArtifactScopeCounts;
  /** Swaps the "Analytical datasets" chip for an "Analyses" chip when true
   *  (analytical datasets are folded into the Analyses filter — see
   *  `ProjectArtifactsListPage`'s `matchesActiveFilter`); pre-flag chip set
   *  otherwise. Defaults to false so callers that don't pass it get
   *  pre-flag behavior.
   *  TODO: cleanup with descriptive_analysis_impl ff */
  descriptiveAnalysisImplEnabled?: boolean;
}

export const ProjectArtifactsToolbar = ({
  activeFilter,
  onFilterChange,
  search,
  onSearchChange,
  createOptions,
  counts,
  countsLoading,
  showFilters = true,
  showSearch = true,
  showCreate = true,
  bordered = true,
  activeScopeFilter = 'all',
  onScopeFilterChange,
  scopeCounts,
  descriptiveAnalysisImplEnabled = false,
}: ProjectArtifactsToolbarProps) => {
  const theme = useTheme();
  const intl = useIntl();
  const { ref: rootRef, value: layout } = useElementWidth<ToolbarLayoutMode>(resolveLayout, 'full');

  const [createAnchor, setCreateAnchor] = useState<HTMLElement | null>(null);
  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  // Search is an icon for split view, and inline field for artifacts only
  const panelLayout = useProjectPanelLayoutContext();
  const searchIsIcon = panelLayout !== 'artifacts-only';
  // Create iconifies once we're past the searchIcon mode.
  const createIsIcon = layout === 'createIcon' || layout === 'narrow';
  // Chips collapse to a dropdown only at the very narrowest layout —
  // above that they stay inline (wrapping into a second row if needed).
  const chipsAreInline = layout !== 'narrow';

  // Auto-collapse the expanded search row if the page switches to
  // `artifacts-only` (where search renders as a persistent inline field instead)
  useEffect(() => {
    if (!searchIsIcon && searchExpanded) {
      setSearchExpanded(false);
    }
  }, [searchIsIcon, searchExpanded]);

  // When the user pops the search input out, focus it.
  useEffect(() => {
    if (searchExpanded) {
      searchInputRef.current?.focus();
    }
  }, [searchExpanded]);

  const openCreateMenu = (event: MouseEvent<HTMLElement>) => {
    setCreateAnchor(event.currentTarget);
  };
  const closeCreateMenu = () => setCreateAnchor(null);
  const openFilterMenu = (event: MouseEvent<HTMLElement>) => {
    setFilterAnchor(event.currentTarget);
  };
  const closeFilterMenu = () => setFilterAnchor(null);

  const handleCreateSelect = (option: CreateOption) => () => {
    closeCreateMenu();
    option.onSelect();
  };

  const handleFilterSelect = (id: ProjectArtifactFilterKind) => () => {
    onFilterChange(id);
    closeFilterMenu();
  };

  // TODO: cleanup with descriptive_analysis_impl ff
  const filterChips = filterChipsFor(descriptiveAnalysisImplEnabled);

  const activeFilterLabel = filterChips.find((c) => c.id === activeFilter)?.label ?? 'All types';

  // Count suffix shown inside each chip — a skeleton while counts resolve,
  // then `(N)`. Rendered as an inline element so it sits on the same line as
  // the chip label (e.g. `Cohorts (4)`).
  const renderCount = (id: ProjectArtifactFilterKind) => {
    if (countsLoading) {
      return (
        <Skeleton
          variant="text"
          width={18}
          aria-hidden
          data-testid={`project-artifacts-filter-count-skeleton-${id}`}
          sx={{ display: 'inline-block' }}
        />
      );
    }
    return (
      <Box component="span" data-testid={`project-artifacts-filter-count-${id}`}>
        ({counts[id] ?? 0})
      </Box>
    );
  };

  const renderChipLabel = (chip: FilterChipDef) => (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <Box component="span">{chip.label}</Box>
      {renderCount(chip.id)}
    </Box>
  );

  const renderFilters = () => {
    if (!chipsAreInline) {
      return (
        <>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FilterListIcon />}
            onClick={openFilterMenu}
            data-testid="project-artifacts-filter-collapsed-button"
            aria-haspopup="menu"
            aria-expanded={Boolean(filterAnchor)}
          >
            {activeFilterLabel}
          </Button>
          <Menu anchorEl={filterAnchor} open={Boolean(filterAnchor)} onClose={closeFilterMenu}>
            {filterChips.map((chip) => (
              <MenuItem
                key={chip.id}
                onClick={handleFilterSelect(chip.id)}
                selected={chip.id === activeFilter}
                data-testid={`project-artifacts-filter-menu-item-${chip.id}`}
              >
                <ListItemText primary={renderChipLabel(chip)} />
              </MenuItem>
            ))}
          </Menu>
        </>
      );
    }
    return (
      <Stack
        direction="row"
        spacing={1}
        flexWrap="wrap"
        rowGap={1}
        data-testid="project-artifacts-filter-chips"
      >
        {filterChips.map((chip) => {
          const selected = chip.id === activeFilter;
          const ChipIcon = ARTIFACT_FILTER_ICON[chip.id];
          return (
            <Chip
              key={chip.id}
              icon={<ChipIcon />}
              label={renderChipLabel(chip)}
              clickable
              size="small"
              variant="outlined"
              onClick={() => onFilterChange(chip.id)}
              data-testid={`project-artifacts-filter-chip-${chip.id}`}
              data-active={selected ? 'true' : 'false'}
              aria-pressed={selected}
              // Greyscale filter chip per the F9 design: a leading type icon
              // (matching the Name-column glyphs), 13px label, neutral border.
              // The active chip reads as a soft grey fill, not a saturated
              // primary tint.
              sx={{
                height: 28,
                borderRadius: '14px',
                borderColor: theme.palette.divider,
                backgroundColor: selected ? theme.palette.action.selected : 'transparent',
                '&:hover': {
                  backgroundColor: selected
                    ? theme.palette.action.selected
                    : theme.palette.action.hover,
                },
                '& .MuiChip-label': {
                  // 6px between the icon and the label (theme.spacing(1.5)).
                  paddingLeft: 1.5,
                  fontSize: 13,
                  fontWeight: selected ? 500 : 400,
                  color: theme.palette.text.primary,
                },
                '& .MuiChip-icon': {
                  marginLeft: 1,
                  marginRight: -0.25,
                  fontSize: 16,
                  color: theme.palette.text.secondary,
                },
              }}
            />
          );
        })}
      </Stack>
    );
  };

  // OR-6615 scope facet — a second chip row (All scopes / Project /
  // Organization) under the kind chips. Always inline (only three short chips);
  // wraps at very narrow widths.
  const renderScopeFacet = () => (
    <Stack
      direction="row"
      spacing={1}
      flexWrap="wrap"
      rowGap={1}
      data-testid="project-artifacts-scope-facet"
    >
      {SCOPE_FACETS.map((facet) => {
        const selected = facet.id === activeScopeFilter;
        const ScopeIcon = ARTIFACT_SCOPE_ICON[facet.id];
        return (
          <Chip
            key={facet.id}
            icon={<ScopeIcon data-testid={`project-artifacts-scope-chip-icon-${facet.id}`} />}
            label={
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                <Box component="span">{intl.formatMessage(facet.label)}</Box>
                {!countsLoading && scopeCounts && (
                  <Box component="span" data-testid={`project-artifacts-scope-count-${facet.id}`}>
                    ({scopeCounts[facet.id] ?? 0})
                  </Box>
                )}
              </Box>
            }
            clickable
            size="small"
            variant="outlined"
            onClick={() => onScopeFilterChange?.(facet.id)}
            data-testid={`project-artifacts-scope-chip-${facet.id}`}
            data-active={selected ? 'true' : 'false'}
            aria-pressed={selected}
            sx={{
              height: 28,
              borderRadius: '14px',
              borderColor: theme.palette.divider,
              backgroundColor: selected ? theme.palette.action.selected : 'transparent',
              '&:hover': {
                backgroundColor: selected
                  ? theme.palette.action.selected
                  : theme.palette.action.hover,
              },
              '& .MuiChip-label': {
                // 6px between the icon and the label (theme.spacing(1.5)),
                // matching the kind row's chips.
                paddingLeft: 1.5,
                paddingRight: 1,
                fontSize: 13,
                fontWeight: selected ? 500 : 400,
                color: theme.palette.text.primary,
              },
              '& .MuiChip-icon': {
                marginLeft: 1,
                marginRight: -0.25,
                fontSize: 16,
                color: theme.palette.text.secondary,
              },
            }}
          />
        );
      })}
    </Stack>
  );

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape' && search) {
      event.preventDefault();
      onSearchChange('');
    }
  };

  const searchInputProps = {
    startAdornment: (
      <InputAdornment position="start">
        <SearchIcon fontSize="small" />
      </InputAdornment>
    ),
    endAdornment: search ? (
      <InputAdornment position="end">
        <IconButton
          size="small"
          edge="end"
          onClick={() => onSearchChange('')}
          aria-label="Clear search"
          data-testid="project-artifacts-search-clear"
        >
          <ClearIcon fontSize="small" />
        </IconButton>
      </InputAdornment>
    ) : null,
  };

  // Full-width layout (artifacts-only) — a persistent inline field, same
  // row as the chips
  const renderInlineSearch = () => (
    <TextField
      size="small"
      placeholder="Search..."
      value={search}
      onChange={(event: ChangeEvent<HTMLInputElement>) => onSearchChange(event.target.value)}
      onKeyDown={handleSearchKeyDown}
      inputProps={{ 'data-testid': 'project-artifacts-search-input' }}
      InputProps={searchInputProps}
      sx={{ width: 220 }}
    />
  );

  const renderSearchToggle = () => (
    <Tooltip title="Search artifacts" arrow disableHoverListener={searchExpanded}>
      <IconButton
        size="small"
        onClick={() => setSearchExpanded(true)}
        data-testid="project-artifacts-search-toggle"
        aria-label="Search artifacts"
        sx={{
          width: 28,
          height: 28,
          borderRadius: `${borderRadius.sm}px`,
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: searchExpanded ? theme.palette.action.selected : 'transparent',
          '&:hover': { backgroundColor: theme.palette.action.hover },
        }}
      >
        <SearchIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  const handleCloseSearch = () => {
    setSearchExpanded(false);
    onSearchChange('');
  };

  const renderExpandedSearchRow = () => (
    <ClickAwayListener
      onClickAway={() => {
        if (!search) setSearchExpanded(false);
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        data-testid="project-artifacts-search-row"
        sx={{ paddingX: 2, paddingTop: 0.5, paddingBottom: 1.5 }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Search by name..."
          value={search}
          onChange={(event: ChangeEvent<HTMLInputElement>) => onSearchChange(event.target.value)}
          onKeyDown={handleSearchKeyDown}
          inputRef={searchInputRef}
          inputProps={{ 'data-testid': 'project-artifacts-search-input' }}
          InputProps={searchInputProps}
        />
        <IconButton
          size="small"
          onClick={handleCloseSearch}
          aria-label="Close search"
          data-testid="project-artifacts-search-close"
        >
          <ClearIcon fontSize="small" />
        </IconButton>
      </Stack>
    </ClickAwayListener>
  );

  const renderCreateTrigger = () => {
    if (createIsIcon) {
      // Icon-only by default, expands to reveal the "Create" label on
      // hover/focus. Matches the filter/search button visual weight in
      // narrow mode while still telegraphing what it does.
      return (
        <Button
          variant="contained"
          size="small"
          onClick={openCreateMenu}
          data-testid="project-artifacts-create-button"
          aria-haspopup="menu"
          aria-expanded={Boolean(createAnchor)}
          aria-label="Create artifact"
          sx={{
            minWidth: 0,
            width: 32,
            height: 32,
            paddingX: 0,
            borderRadius: '16px',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            transition: theme.transitions.create(['width', 'padding'], {
              duration: theme.transitions.duration.shorter,
            }),
            '& .toolbar-create-label': {
              maxWidth: 0,
              opacity: 0,
              marginLeft: 0,
              transition: theme.transitions.create(['max-width', 'opacity', 'margin-left'], {
                duration: theme.transitions.duration.shorter,
              }),
            },
            '&:hover, &:focus-visible': {
              width: 'auto',
              paddingX: theme.spacing(2),
              '& .toolbar-create-label': {
                maxWidth: theme.spacing(20),
                opacity: 1,
                marginLeft: theme.spacing(1),
              },
            },
          }}
        >
          <AddIcon fontSize="small" />
          <Box component="span" className="toolbar-create-label">
            Create
          </Box>
        </Button>
      );
    }
    return (
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        endIcon={<KeyboardArrowDownIcon />}
        onClick={openCreateMenu}
        data-testid="project-artifacts-create-button"
        aria-haspopup="menu"
        aria-expanded={Boolean(createAnchor)}
      >
        Create
      </Button>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        borderBottom: bordered ? `1px solid ${theme.palette.divider}` : 'none',
        minWidth: 0,
      }}
    >
      <Stack
        ref={rootRef}
        data-testid={
          showFilters ? 'project-artifacts-toolbar' : 'project-artifacts-toolbar-create-only'
        }
        data-layout={layout}
        direction="row"
        spacing={2}
        sx={{
          alignItems: 'center',
          justifyContent: showFilters ? 'space-between' : 'flex-end',
          paddingX: bordered ? 2 : 0,
          paddingY: bordered ? 1.5 : 0,
          flexWrap: 'nowrap',
          minWidth: 0,
        }}
      >
        {showFilters && (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                {renderFilters()}
              </Box>
              {renderScopeFacet()}
            </Stack>
          </Box>
        )}

        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexShrink: 0 }}>
          {showSearch &&
            (searchIsIcon ? !searchExpanded && renderSearchToggle() : renderInlineSearch())}
          {showCreate && (
            <>
              {renderCreateTrigger()}
              <Menu anchorEl={createAnchor} open={Boolean(createAnchor)} onClose={closeCreateMenu}>
                {createOptions.map((option) => {
                  const KindIcon = ARTIFACT_KIND_ICON[option.id];
                  return (
                    <MenuItem
                      key={option.id}
                      onClick={handleCreateSelect(option)}
                      data-testid={`project-artifacts-create-menu-${option.id}`}
                    >
                      <ListItemIcon
                        sx={{ color: theme.palette.text.primary, minWidth: theme.spacing(4) }}
                      >
                        <KindIcon
                          fontSize="small"
                          data-testid={`project-artifacts-create-menu-icon-${option.id}`}
                        />
                      </ListItemIcon>
                      <ListItemText>{option.label}</ListItemText>
                    </MenuItem>
                  );
                })}
              </Menu>
            </>
          )}
        </Stack>
      </Stack>
      {showSearch && searchIsIcon && searchExpanded && renderExpandedSearchRow()}
    </Box>
  );
};
